import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Description as DocumentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import HtmlEditor from "./HtmlEditor";
import HtmlViewer from "./HtmlViewer";
import { showErrorToast } from "utils/toastNotification";

export default function DocumentEditorViewerModal({
  open,
  onClose,
  meeting,
  onSaveDocument,
  loading = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [isEditing, setIsEditing] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docContent, setDocContent] = useState("");

  useEffect(() => {
    if (meeting) {
      setDocTitle(meeting.document_title || `${meeting.title || "Meeting"} - Document`);
      setDocContent(meeting.document_content || "");
      if (!meeting.document_content) {
        setIsEditing(true);
      } else {
        setIsEditing(false);
      }
    }
  }, [meeting, open]);

  const handleSave = async () => {
    if (!docTitle.trim()) {
      showErrorToast("Document title is required");
      return;
    }
    const payload = {
      meeting_id: meeting?.id,
      document_id: meeting?.document_id || null,
      title: docTitle.trim(),
      content: docContent,
    };
    await onSaveDocument(payload);
    setIsEditing(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "#111c44" : "#ffffff",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
          py: 2.5,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: isDark ? "rgba(139, 92, 246, 0.15)" : "#f5f3ff",
              color: "#8b5cf6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DocumentIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? "#ffffff" : "#1b2559" }}>
              {isEditing ? "Prepare Meeting Notes & Minutes" : "Meeting Minutes & Notes"}
            </Typography>
            <Typography sx={{ color: isDark ? "#a3aed0" : "#707eae", fontSize: "0.8rem" }}>
              For: <span style={{ fontWeight: 700, color: isDark ? "#ffffff" : "#1b2559" }}>{meeting?.title}</span>
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {!isEditing && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setIsEditing(true)}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontSize: "0.8rem",
                fontWeight: 700,
                borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
                color: isDark ? "#ffffff" : "#1b2559",
                "&:hover": { borderColor: "#f59e0b" },
              }}
            >
              Edit
            </Button>
          )}
          {isEditing && meeting?.document_content && (
            <Button
              size="small"
              variant="text"
              startIcon={<ViewIcon fontSize="small" />}
              onClick={() => setIsEditing(false)}
              sx={{ textTransform: "none", fontSize: "0.8rem", fontWeight: 600, color: "text.secondary" }}
            >
              Cancel Edit
            </Button>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: isDark ? "#a3aed0" : "#707eae" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {isEditing ? (
          <Stack spacing={2.5}>
            <TextField
              label="Document Title"
              placeholder="e.g. Minutes of Meeting (MOM) / Agenda"
              fullWidth
              size="small"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              InputProps={{ sx: { borderRadius: "12px" } }}
            />

            <Box>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
                Meeting Notes & Discussion:
              </Typography>
              <HtmlEditor
                value={docContent}
                onChange={(html) => setDocContent(html)}
                placeholder="Prepare meeting agenda, discussion notes, decisions, and action items..."
              />
            </Box>
          </Stack>
        ) : (
          <HtmlViewer
            title={meeting?.document_title}
            htmlContent={meeting?.document_content}
            updatedAt={meeting?.document_updated_at}
            onEdit={() => setIsEditing(true)}
            showActions={true}
          />
        )}
      </DialogContent>

      {isEditing && (
        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            py: 2,
            px: 3,
            bgcolor: isDark ? "#0b1437" : "#f8fafc",
          }}
        >
          <Button onClick={() => setIsEditing(false)} color="inherit" sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSave}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "10px",
              px: 3,
              py: 0.9,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            Save Document
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
